import { carousel } from './carousel';

/**
 * # Gallery — thumbnail carousel + shared lightbox
 *
 * Composes the reusable {@link carousel} (the thumbnail strip) with a full-size
 * lightbox `<dialog>`. Both `modgallery` display modes (`list_main`,
 * `list_content`) and the news opening gallery (`news_image_gallery.php`) use
 * this single component; the lightbox markup is the shared `item_lightbox.php`
 * (slide list passed in via the `list` view argument).
 *
 * ## Composition
 * The factory **spreads** `carousel()` so the gallery *is* a carousel (header /
 * bottom arrows drive `prev()` / `next()`, `current` tracks the centered thumb)
 * and adds the lightbox on top. `carousel()` exposes its lifecycle as both
 * `init()` / `destroy()` (for standalone `x-data="carousel"` use) *and* the
 * lower-level `initCarousel()` / `destroyCarousel()`. This component defines its
 * own `init()` / `destroy()` *after* the spread, overriding the carousel's, so
 * it owns the Alpine lifecycle and drives both layers (carousel + lightbox). The
 * carousel factory deliberately uses plain reactive props (no getters) so the
 * spread keeps them reactive.
 *
 * ## Lightbox index vs carousel index
 * The thumbnail carousel (`current`) and the lightbox (`lightIndex`) are kept
 * **independent**: opening the lightbox on a thumbnail that isn't centered must
 * not yank the strip, and paging the lightbox shouldn't reflow the page behind
 * the backdrop. Both read the same ordered `items` list (built from the slides'
 * `data-photo-id`).
 *
 * ## URL scheme
 * Opening pushes `#photo-{id}` via `history.pushState`; `hashchange` syncs the
 * lightbox (back/forward + deep-linking a shared `#photo-{id}` URL opens the
 * right image). Closing restores `pathname + search`.
 *
 * ## ASwap teardown
 * ASwap swaps page content without a reload. Alpine's MutationObserver fires
 * `destroy()` on the removed component, which removes the `hashchange` listener
 * and closes the native dialog — so a lightbox left open never lingers over the
 * incoming page (belt-and-braces with `WCAG.dialogCloseAll()` in the aswap hook).
 *
 * ## Animations
 * - Open: CSS / the native `<dialog>` top-layer handles the entrance.
 * - Navigate: each lightbox figure is shown by `lightIndex === i` and carries its
 *   own Alpine x-transition, so paging cross-fades the outgoing/incoming image
 *   with no JS timers (the figures are absolutely stacked in the stage).
 *
 * ## Touch swipe (lightbox)
 * The stage is an absolutely-stacked figure list (no native horizontal scroll),
 * so paging by swipe is wired explicitly on the stage element (`$refs.stage`),
 * same pattern as `heroCarousel`: a **touch** drag past {@link SWIPE_THRESHOLD}
 * (and more horizontal than vertical) pages one step (left → `lightNext`, right →
 * `lightPrev`). Only touch pointers are handled, so the arrow / close buttons keep
 * working with mouse/pen. The stage carries `touch-pan-y` so a vertical drag is
 * left to the browser. A completed swipe suppresses the trailing `click` so the
 * figure's tap-to-close (`@click.self`) doesn't also fire.
 */
type Refs = { $refs: { dialog?: HTMLDialogElement; stage?: HTMLElement } };

/** Minimum horizontal touch travel (px) that counts as a lightbox swipe, not a tap. */
const SWIPE_THRESHOLD = 40;

/**
 * Lock / unlock page scroll behind the modal lightbox. The dialog is modal, but
 * Lenis keeps hijacking wheel/touch globally, so the page would still scroll
 * behind the backdrop — same lock the offcanvas store uses (Lenis `.stop()` +
 * an `html.is-scroll-locked` backstop).
 */
const lockPageScroll = (lock: boolean): void => {
  const lenis = (window as unknown as { lenis?: { stop(): void; start(): void } }).lenis;
  document.documentElement.classList.toggle('is-scroll-locked', lock);
  lock ? lenis?.stop() : lenis?.start();
};

export function gallery() {
  return {
    ...carousel(),

    isOpen: false as boolean,
    lightIndex: 0,
    items: [] as { id: string }[],
    _trigger: null as HTMLElement | null,
    _hashHandler: null as (() => void) | null,
    _stage: null as HTMLElement | null,
    _swipeStartX: null as number | null,
    _swipeStartY: null as number | null,
    _swiped: false,
    _onSwipeDown: null as ((e: PointerEvent) => void) | null,
    _onSwipeUp: null as ((e: PointerEvent) => void) | null,
    _onSwipeCancel: null as (() => void) | null,
    _onSwipeClick: null as ((e: Event) => void) | null,

    init(): void {
      this.initCarousel();
      // The lightbox reads the same ordered list as the thumbnail strip.
      this.items = this._slides.map((s) => ({ id: s.dataset.photoId ?? '' }));
      this._hashHandler = (): void => this.checkHash();
      window.addEventListener('hashchange', this._hashHandler);
      this._initSwipe();
      this.checkHash();
    },

    destroy(): void {
      this.destroyCarousel();
      if (this._hashHandler) {
        window.removeEventListener('hashchange', this._hashHandler);
      }
      this._destroySwipe();
      const dialog = (this as unknown as Refs).$refs.dialog;
      if (dialog?.open) {
        dialog.close();
      }
      // Released here too: an ASwap teardown while the lightbox is open must not
      // leave the incoming page scroll-locked.
      if (this.isOpen) {
        lockPageScroll(false);
      }
    },

    /** Wire touch-swipe paging on the lightbox stage (touch pointers only). */
    _initSwipe(): void {
      const stage = (this as unknown as Refs).$refs.stage;
      if (!stage) {
        return;
      }
      this._stage = stage;
      this._onSwipeDown = (e: PointerEvent): void => {
        if (e.pointerType !== 'touch') {
          return;
        }
        this._swipeStartX = e.clientX;
        this._swipeStartY = e.clientY;
        stage.setPointerCapture(e.pointerId);
      };
      this._onSwipeUp = (e: PointerEvent): void => {
        if (this._swipeStartX === null || this._swipeStartY === null) {
          return;
        }
        const dx = e.clientX - this._swipeStartX;
        const dy = e.clientY - this._swipeStartY;
        this._swipeStartX = null;
        this._swipeStartY = null;
        // Horizontal swipe past the threshold pages; vertical is left to scroll.
        if (Math.abs(dx) >= SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
          this._swiped = true;
          if (dx < 0) {
            this.lightNext();
          } else {
            this.lightPrev();
          }
        }
      };
      this._onSwipeCancel = (): void => {
        this._swipeStartX = null;
        this._swipeStartY = null;
      };
      // A completed swipe must not also trigger the figure's tap-to-close click.
      this._onSwipeClick = (e: Event): void => {
        if (this._swiped) {
          e.stopPropagation();
          e.preventDefault();
          this._swiped = false;
        }
      };
      stage.addEventListener('pointerdown', this._onSwipeDown);
      stage.addEventListener('pointerup', this._onSwipeUp);
      stage.addEventListener('pointercancel', this._onSwipeCancel);
      stage.addEventListener('click', this._onSwipeClick, true);
    },

    _destroySwipe(): void {
      const stage = this._stage;
      if (!stage) {
        return;
      }
      if (this._onSwipeDown) {
        stage.removeEventListener('pointerdown', this._onSwipeDown);
      }
      if (this._onSwipeUp) {
        stage.removeEventListener('pointerup', this._onSwipeUp);
      }
      if (this._onSwipeCancel) {
        stage.removeEventListener('pointercancel', this._onSwipeCancel);
      }
      if (this._onSwipeClick) {
        stage.removeEventListener('click', this._onSwipeClick, true);
      }
      this._stage = null;
    },

    get srLabel(): string {
      return this.isOpen ? `${this.lightIndex + 1} / ${this.items.length}` : '';
    },

    openLightbox(index: number, pushHash = true): void {
      const self = this as unknown as Refs & { $nextTick: (cb: () => void) => void };
      const dialog = self.$refs.dialog;
      if (!dialog) {
        return;
      }
      // Set the index first so the correct figure is shown before the dialog
      // paints — otherwise opening on a non-zero index flashes image 0.
      this.lightIndex = index;
      if (!this.isOpen) {
        // Remember the trigger so focus returns to it on close.
        this._trigger = document.activeElement as HTMLElement;
        this.isOpen = true;
        lockPageScroll(true);
        self.$nextTick(() => dialog.showModal());
      }
      // Already open: per-figure x-transition cross-fades the swap.
      if (pushHash && this.items[index]) {
        history.pushState(null, '', `#photo-${this.items[index].id}`);
      }
    },

    closeLightbox(clearHash = true): void {
      const dialog = (this as unknown as Refs).$refs.dialog;
      this.isOpen = false;
      lockPageScroll(false);
      if (dialog?.open) {
        dialog.close();
      }
      this._trigger?.focus();
      if (clearHash && location.hash.startsWith('#photo-')) {
        history.pushState(null, '', location.pathname + location.search);
      }
    },

    lightNext(): void {
      if (this.lightIndex < this.items.length - 1) {
        this.openLightbox(this.lightIndex + 1);
      }
    },

    lightPrev(): void {
      if (this.lightIndex > 0) {
        this.openLightbox(this.lightIndex - 1);
      }
    },

    /** Open / close / sync the lightbox from a `#photo-{id}` hash (deep-link + back/fwd). */
    checkHash(): void {
      const m = location.hash.match(/^#photo-(.+)$/);
      if (m) {
        const idx = this.items.findIndex((it) => it.id === m[1]);
        if (idx >= 0) {
          this.openLightbox(idx, false);
          return;
        }
      }
      if (this.isOpen) {
        this.closeLightbox(false);
      }
    },
  };
}
